import * as React from 'react';
import RBush from 'rbush';

import { useMapContext } from '../../context';
import { Desktop } from '../../../../../packages/Responsive';
import { IRBushItem } from '../../../../types/map';
import { FeaturesContext } from './context';
import { useApplicationContext } from '../../../ApplicationContext';
import { ClickHandlers } from './ClickHandlers';
import { DetailsVisibilityManager } from './DetailsVisibilityManager';
import { DotActive } from './DotActive';
import { DotHover } from './DotHover';
import { FavoritesUpdater } from './FavoritesUpdater';
import { FeaturesPropertiesUpdater } from './FeaturesPropertiesUpdater';
import { FeaturesUpdater } from './FeaturesUpdater';
import { NotFoundContainer } from './NotFoundContainer';
import { PopupManager } from './PopupManager';
import { ResultabilityManager } from './ResultabilityManager';
import { SubdomainUpdater } from './SubdomainUpdater';

export const Features: React.FC = () => {
  const { config } = useApplicationContext();
  const { ymaps, map } = useMapContext();
  const rbushRef = React.useRef(new RBush<IRBushItem>());
  const pointsLayerRef = React.useRef(
    new ymaps.PointsLayer({
      map,
      rbush: rbushRef.current,
    }),
  );
  const hotspotsLayerRef = React.useRef(new ymaps.HotspotsLayer({ map, rbush: rbushRef.current }));
  const balloonManagerRef = React.useRef(new ymaps.BalloonManager({ map, rbush: rbushRef.current }));
  const popupManagerRef = React.useRef(new ymaps.PopupManager({ map }));
  const [isDetailsVisible, changeIsDetailsVisible] = React.useState(false);
  const [isResultsAvailable, changeIsResultsAvailable] = React.useState(false);
  const isPopupEnabled = config.get<boolean>('pinPopup.enabled');

  const handleVisibilityChange = React.useCallback((isDetailsVisible: boolean) => {
    changeIsDetailsVisible(isDetailsVisible);
  }, []);

  const handleResultabilityChange = React.useCallback((isResultable: boolean) => {
    changeIsResultsAvailable(isResultable);
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    map.layers.add(pointsLayerRef.current.layer);
    map.layers.add(hotspotsLayerRef.current.layer);
    map.geoObjects.add(balloonManagerRef.current.objectManager);

    return () => {
      map.layers.remove(pointsLayerRef.current.layer);
      map.layers.remove(hotspotsLayerRef.current.layer);
      map.geoObjects.remove(balloonManagerRef.current.objectManager);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <FeaturesContext.Provider
      value={{
        rbush: rbushRef.current,
        pointsLayer: pointsLayerRef.current,
        hotspotsLayer: hotspotsLayerRef.current,
        balloonManager: balloonManagerRef.current,
        popupManager: popupManagerRef.current,
      }}
    >
      <ClickHandlers resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <DetailsVisibilityManager onVisibilityChange={handleVisibilityChange} />
      {isPopupEnabled && (
        <Desktop>
          <PopupManager resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
        </Desktop>
      )}
      <DotActive resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <DotHover resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <FavoritesUpdater />
      <FeaturesPropertiesUpdater />
      <FeaturesUpdater detailsVisible={isDetailsVisible} />
      <NotFoundContainer />
      <ResultabilityManager onResultabilityChange={handleResultabilityChange} />
      <SubdomainUpdater />
    </FeaturesContext.Provider>
  );
};
